<template>
  <el-dialog
    :visible="true"
    width="1050px"
    :close-on-click-modal="false"
    :before-close="handleClose"
    :fullscreen="true"
  >
    <div slot="title">
      {{ title }}
      <el-tag
        v-if="customerOrder.status && (type === 'update' || type === 'view')"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <el-alert
      v-if="customerOrder.status === 'done' && type !== 'view'"
      title="Ez a vevői megrendelés már le lett zárva, ezért nem módosítható."
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 30px; margin-top: -20px;"
    />
    <div v-if="type === 'view' && customerOrder.receipt_number && customerOrder.status !== 'cancelled'">
      <receipt-link
        :id="customerOrder.id"
        :label="customerOrder.receipt_number"
        type="customerOrder"
      />
    </div>
    <div v-loading="loading">
      <el-form
        ref="orderForm"
        label-position="top"
        :model="customerOrder"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="supplier"
              label="Szállító"
            >
              <el-select
                v-model="customerOrder.supplier"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="customerOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Szállitó raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Ebből a raktárból küldjük vissza az árut. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                    placement="right"
                  >
                    <i class="el-icon-info"/>
                  </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="customerOrder.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="customerOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer"
              label="Vevő"
            >
              <el-select
                v-model="customerOrder.customer"
                style="width: 100%;"
                value-key="id"
                filterable
                remote
                :remote-method="searchPartners"
                :loading="partnerLoading"
                placeholder="Írd be a partner nevét a kereséshez..."
                :disabled="customerOrder.status !== 'recorded' || type === 'view'"
                @change="onCustomerChange"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'customer'"
                  :value="customer"
                  :label="`#${customer.id} ${customer.company.name}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer_address"
              label="Vevő szállítási címe"
            >
              <el-select
                v-model="customerOrder.customer_address"
                v-loading="addressesLoading"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="customerOrder.status !== 'recorded' || addresses.length === 0 || type === 'view'"
              >
                <el-option
                  v-for="address in addresses"
                  :key="address.id + 'address'"
                  :value="address"
                  :label="`${address.full_address}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="order_numbers"
              label="Megdendelésszám"
            >
              <el-input
                v-model="customerOrder.order_numbers"
                :disabled="customerOrder.status !== 'recorded' || type === 'view'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="customerOrder.currency"
                :disabled="customerOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="delivery_date"
              label="Szállítás dátuma"
            >
              <el-date-picker
                :key="dateKey"
                v-model="customerOrder.delivery_date"
                type="date"
                :disabled="customerOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
                @input="dateKey++"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          :currency="customerOrder.currency && customerOrder.currency.code"
          :is-product-select-from-stock="customerOrder.status !== 'done' && customerOrder.status !== 'sent' && customerOrder.status !== 'cancelled'"
          :load-base-count-from-stock="false"
          :default-items="customerOrder.order_items"
          :enabled-elements="enabledElements"
          price-type="price"
          :custom-prices="customPrices"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'supplierSelector', 'comment'] : ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions', 'supplierSelector']"
          compare-type="completion"
          :storage-id="customerOrder.storage && customerOrder.storage.id"
          :compare-items="customerOrder.sold_items && customerOrder.sold_items.length ? customerOrder.sold_items : null"
          @priceChange="onPriceChange"
        />

      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ customerOrder.net_price | currency(customerOrder.currency.code) }}</strong></div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ customerOrder.gross_price | currency(customerOrder.currency.code) }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="customerOrder.status === 'sent'"
        type="warning"
        :loading="cancelButtonLoading"
        @click="cancel"
      >
        Rendelés lemondása
      </el-button>
      <el-button
        v-if="customerOrder.status === 'cancelled'"
        type="success"
        :loading="restoreButtonLoading"
        @click="restore"
      >
        Lemondás visszavonása
      </el-button>
      <el-button
        :disabled="customerOrder.status !== 'recorded' || loading"
        type="primary"
        :loading="buttonLoading"
        @click="store"
      >
        {{ buttonText }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import ProductItems from '../../utils/ProductItems';
import ReceiptLink from '../../utils/ReceiptLink';

export default {
  components: {
    ProductItems,
    ReceiptLink,
  },
  props: ['type', 'customerOrderId'],
  data() {
    return {
      loading: false,
      addressesLoading: false,
      receiptPrefix: '',
      buttonLoading: false,
      partnerLoading: false,
      cancelButtonLoading: false,
      restoreButtonLoading: false,
      dateKey: 0,
      addresses: [],
      customers: [],
      customPrices: [],
      customerOrder: {
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        storage: {},
        target_storage: {},
        customer: null,
        supplier: {},
        completed_at: null,
        currency: {},
        customer_address: {},
      },
      rules: {
        customer: [
          {
            required: true,
            message: 'Vevő kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        supplier: [
          {
            required: true,
            message: 'Beszállító kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        target_storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        completed_at: [
          {
            required: true,
            message: 'Teljesítés megadása kötelező',
            trigger: 'change'
          }
        ],
        currency: [
          {
            required: true,
            message: 'Deviza megadása kötelező',
            trigger: 'change'
          }
        ],
      },
    };
  },
  computed: {
    title() {
      if (this.type === 'add') {
        return 'Új vevői megrendelés';
      } else if (this.type === 'update') {
        return 'Vevői megrendelés módosítása';
      } else if (this.type === 'view') {
        return 'Vevői megrendelés megtekintése';
      } else {
        return '';
      }
    },
    buttonText() {
      if (this.type === 'add') {
        return 'Hozzáadása';
      } else if (this.type === 'update') {
        return 'Módosítás';
      } else {
        return '';
      }
    },
    enabledElements() {
      if(this.type === 'view') {
        return [];
      }
      switch (this.customerOrder.status) {
        case 'recorded':
          return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDateSelector', 'supplierSelector'];
        case 'sent':
          return [];
        case 'done':
          return [];
        default:
          return [];
      }
    },
    suppliers() {
      return this.$store.getters['ownCompany/ownCompanies'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
    currencies() {
      return this.$store.getters['currency/currencies'];
    },
  },
  async mounted() {
    this.loading = true;
    const requests = [];
    requests.push(await axios.get('api/receipt-prefixes/customerOrder'));
    if (!this.currencies || !this.currencies.length) {
      requests.push(this.$store.dispatch('currency/fetchAll'));
    }
    if (!this.storages || !this.storages.length) {
      requests.push(this.$store.dispatch('storage/fetchAll'));
    }
    if (!this.suppliers || !this.suppliers.length) {
      requests.push(this.$store.dispatch('ownCompany/fetchAll'));
    }
    const [prefix] = await Promise.all(requests);
    this.receiptPrefix = prefix.full_prefix;
    if (this.customerOrderId) {
      this.customerOrder = await this.$store.dispatch('customerOrder/fetch', this.customerOrderId);
      this.customers.push(this.customerOrder.customer);
      this.customPrices = await axios.get(`api/customer-price-lists/partner/${this.customerOrder.customer.id}`);
      this.addresses = this.customerOrder.customer.company.addresses.filter(a => a.pivot.type === 'delivery');
      if(this.type === 'add') {
        this.customerOrder.status = 'recorded';
        this.customerOrder.receipt_number = null;
        this.customerOrder.order_items = this.customerOrder.order_items.map(item => {
          delete item.id;
          return item;
        });
      }
      if(this.type !== 'add') {
        this.customerOrder.sold_items = await axios.get('api/customer-orders/sold-items/' + this.customerOrder.id);
      }
    } else {
      this.customerOrder.supplier = this.suppliers[0];
      this.customerOrder.currency = this.currencies[0];
      this.customerOrder.storage = this.storages[0];
      this.customerOrder.delivery_date = moment()
        .add(3, 'days')
        .format('Y-MM-DD');
    }
    this.loading = false;
  },
  methods: {
    async searchPartners(query) {
      this.partnerLoading = true;
      if (!query) {
        this.partners = [];
      } else {
        const queryString = new URLSearchParams({
          name: query,
        });
        this.customers = await this.$store.dispatch('partner/search', '?' + queryString.toString());
      }
      this.partnerLoading = false;
    },
    async onCustomerChange() {
      this.addressesLoading = true;
      this.addresses = await axios.get(`api/addresses/company/${this.customerOrder.customer.company.id}/delivery`);
      if (this.addresses) {
        this.customerOrder.customer_address = this.addresses[0];
      }
      this.customPrices = await axios.get(`api/customer-price-lists/partner/${this.customerOrder.customer.id}`);
      this.addressesLoading = false;
    },
    onPriceChange({ netPrice, grossPrice }) {
      this.customerOrder.net_price = netPrice;
      this.customerOrder.gross_price = grossPrice;
    },
    handleClose() {
      if(this.type === 'view') {
        return this.$router.replace('/sales/customer-orders');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/sales/customer-orders');
        })
        .catch(() => {
        });
    },
    async cancel() {
      this.cancelButtonLoading = true;
      await this.$store.dispatch('customerOrder/cancel', this.customerOrder.id);
      this.loading = true;
      await this.$store.dispatch('customerOrder/fetchAll');
      this.customerOrder = await this.$store.dispatch('customerOrder/fetch', this.customerOrderId);
      this.$refs['productItems'].calculateFullPrice();
      this.loading = false;
      this.cancelButtonLoading = false;
    },
    async restore() {
      this.restoreButtonLoading = true;
      await this.$store.dispatch('customerOrder/undoCancel', this.customerOrder.id);
      this.loading = true;
      await this.$store.dispatch('customerOrder/fetchAll');
      this.customerOrder = await this.$store.dispatch('customerOrder/fetch', this.customerOrderId);
      this.$refs['productItems'].calculateFullPrice();
      this.loading = false;
      this.restoreButtonLoading = false;
    },
    async store() {
      this.$refs.orderForm.validate(async valid => {
        if (valid) {
          this.customerOrder.order_items = this.$refs['productItems'].getItems();
          if (!this.customerOrder.order_items.length) {
            return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
          } else if (!this.$refs['productItems'].validateProducts()) {
            return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
          }
          this.buttonLoading = true;
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('customerOrder/create', this.customerOrder);
            } else if (this.type === 'update') {
              await this.$store.dispatch('customerOrder/update', this.customerOrder);
            }
            this.$store.dispatch('customerOrder/fetchAll');
            this.buttonLoading = false;
            this.$router.replace('/sales/customer-orders');
          } catch (e) {
          }
          this.buttonLoading = false;
        }
      });
    },
    getStatus() {
      switch (this.customerOrder.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'sent':
          return {
            label: 'Rakodási listán',
            tag: 'primary',
          };
        case 'cancelled':
          return {
            label: 'Lemondva',
            tag: 'danger',
          };
        case 'partly-done':
          return {
            label: 'Részben teljesítve',
            tag: 'warning',
          };
        case 'done':
          return {
            label: 'Teljesítve',
            tag: 'success',
          };
      }
    },
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;

    > div {
      display: inline-block;
    }
  }
</style>
